import {
  type ConnectorOperation,
  connectionMethodUsable,
  type InstalledPlugin,
  isMarketplacePluginSource,
  type PluginCatalogItem,
  type PluginContributionKind,
  pluginContributionKey,
} from '@crowdex/plugin-contracts';
import { Switch } from '@crowdex/ui/components/switch';
import { cn } from '@crowdex/ui/lib/utils';
import {
  ArrowLeft,
  Check,
  ExternalLink,
  KeyRound,
  LoaderCircle,
  Plug,
  RefreshCw,
  Trash2,
  Upload,
} from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { settingButtonClass } from '@/components/settings-page/setting-controls';
import { openAppHome } from '@/lib/app-home';
import { useRouter } from '@/lib/navigation';
import { nativePluginClient } from '@/lib/plugins/plugin-native-client';
import { pluginPagePath, servicePluginPagePath } from '@/lib/plugins/plugin-sections';
import {
  isPluginConfigurable,
  isPluginConnectionConfigurable,
  isPluginSourceConfigurable,
} from '@/lib/plugins-page/plugin-status';
import { HubConnections } from './hub-connections';
import { PluginAgentActions } from './plugin-agent-actions';
import { CONTRIBUTION_LABELS, pluginKindLabel, SOURCE_LABELS } from './plugin-card';
import { PluginServiceConnections } from './plugin-connections';
import { PluginContents, pluginContentGroups } from './plugin-contents';
import { GrantList, permissionLabel } from './plugin-grants';
import { PluginLogo } from './plugin-logo';
import { PluginDiagnostics, PluginUsedFor } from './plugin-used-for';
import { UnsandboxedCommands } from './unsandboxed-commands';

/** What each kind of contribution does, for a plugin not installed yet. */
const CONTRIBUTION_DETAILS: Record<PluginContributionKind, string> = {
  connectors: 'Actions and triggers of a service, for agents and automations.',
  skills: 'Instructions agents load when a task needs them.',
  mcp: 'MCP servers whose tools agents can call.',
  agents: 'Agents you can start conversations with.',
  resources: 'Files agents can read as MCP resources.',
  themes: 'Color themes for the app, the code and the terminal, in Settings › General.',
  apps: 'An app of another assistant (ChatGPT); Crowdex uses its connector when it has one.',
};

/** `google_calendar` → `Google calendar`, for a service known only by its id. */
function serviceTitle(service: string): string {
  const id = service.split('/').at(-1) ?? service;
  const words = id.replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

type AccountRow = {
  key: string;
  title: string;
  detail: string;
  state: 'connected' | 'needed' | 'optional';
  action: { label: string; onClick: () => void };
};

/**
 * The accounts a plugin uses: the connection types it declares itself (`<plugin>/<service>`), whose
 * accounts are managed in its page, and the other services it uses, each a row leading to its page.
 */
function pluginAccounts(
  plugin: InstalledPlugin,
  /** Opens the page of another service the plugin uses, where its accounts are. */
  connect: (service: string) => void,
): { own: string[]; rows: AccountRow[] } {
  const required = new Map(
    (plugin.requires?.connections ?? []).map((connection) => [
      connection.service,
      connection.optional,
    ]),
  );
  // The plugin's own service is connected in its Connect section; other services it uses are here.
  const services = [
    ...new Set([...required.keys(), ...(plugin.connectors ?? []).map((item) => item.service)]),
  ].filter((service) => service !== plugin.service);
  const own = services.filter((service) => service.startsWith(`${plugin.name}/`));
  const rows = services
    .filter((service) => !own.includes(service))
    .map(
      (service): AccountRow => ({
        key: `service:${service}`,
        title: serviceTitle(service),
        detail:
          required.get(service) === true
            ? 'A connection unlocks more of this plugin. Connections are shared by every plugin of the service.'
            : 'Agents and automations act with this connection. It is shared by every plugin of the service.',
        state: required.get(service) === true ? 'optional' : 'needed',
        action: { label: 'Manage connections', onClick: () => connect(service) },
      }),
    );
  return { own, rows };
}

function AccountRowView({ account, busy }: { account: AccountRow; busy: boolean }) {
  return (
    <div className="flex items-center gap-3 border-hairline border-b px-4 py-3 last:border-b-0">
      <span
        className={cn(
          'flex size-7 shrink-0 items-center justify-center rounded-full',
          account.state === 'connected'
            ? 'bg-success/10 text-success'
            : account.state === 'needed'
              ? 'bg-attention/10 text-attention'
              : 'bg-control text-muted-foreground',
        )}
      >
        {account.state === 'connected' ? (
          <Check className="size-3.5" />
        ) : account.key.startsWith('service:') ? (
          <Plug className="size-3.5" />
        ) : (
          <KeyRound className="size-3.5" />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm">
          {account.title}
          {account.state === 'optional' && (
            <span className="text-muted-foreground"> · optional</span>
          )}
        </span>
        <span className="mt-0.5 block text-muted-foreground text-xs">{account.detail}</span>
      </span>
      <button
        className={settingButtonClass()}
        disabled={busy}
        onClick={account.action.onClick}
        type="button"
      >
        {account.action.label}
      </button>
    </div>
  );
}

function Section({
  id,
  title,
  count,
  aside,
  children,
}: {
  id?: string;
  title: string;
  count?: number;
  /** What sits at the end of the title row (a switch). */
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="mt-7 scroll-mt-6" id={id}>
      <div className="mb-2.5 flex items-center gap-2">
        <h2 className="font-semibold text-muted-foreground text-sm">{title}</h2>
        {count !== undefined && <span className="text-muted-foreground text-xs">· {count}</span>}
        {aside ? <span className="ml-auto flex items-center gap-2">{aside}</span> : null}
      </div>
      {children}
    </section>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-hairline border-b py-2 text-sm last:border-b-0">
      <dt className="shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words text-right text-foreground">{children}</dd>
    </div>
  );
}

const SETTINGS_ID = 'plugin-settings';
const CONNECT_ID = 'plugin-connect';

/** What it brings, in a few tiles: an installed plugin's own lists, else its catalog kinds. */
function broughtSummary(
  plugin: PluginCatalogItem | InstalledPlugin,
  local: InstalledPlugin | undefined,
): { title: string; detail: string }[] {
  if (local !== undefined)
    return pluginContentGroups(local).map((group) => ({
      title: group.title,
      detail:
        group.rows.length > 2
          ? `${group.rows.length} ${group.title.toLowerCase()}`
          : group.rows.map((row) => row.label).join(', '),
    }));
  return plugin.contributes.map((kind) => ({
    title: CONTRIBUTION_LABELS[kind],
    detail: CONTRIBUTION_DETAILS[kind],
  }));
}

/** What a service's triggers wake a conversation on (D30 "Wakes on"): their titles. */
export function wakesOn(operations: readonly ConnectorOperation[]): string[] {
  return operations
    .filter((operation) => operation[2] === 'trigger')
    .map((operation) => operation[1]);
}

/** The triggers of a plugin's service, as the hub's catalog lists them; none while loading. */
function useServiceTriggers(service: string | null): string[] {
  const [triggers, setTriggers] = useState<string[]>([]);
  useEffect(() => {
    setTriggers([]);
    if (service === null) return;
    let cancelled = false;
    nativePluginClient.connectorService(service).then(
      (actions) => !cancelled && setTriggers(wakesOn(actions.operations)),
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
  }, [service]);
  return triggers;
}

/** Whether connecting an account is part of installing it: a service with a way that needs one. */
function needsAccount(plugin: PluginCatalogItem): boolean {
  return (
    plugin.service !== null &&
    (plugin.methods ?? []).some(
      (method) => connectionMethodUsable(method) && method.kind !== 'none',
    )
  );
}

/**
 * A plugin's page (D30, phone 27): what it brings, what it may do and the accounts it uses, with
 * one main button — Install, then Connect when an account is still missing — and the rest
 * of its controls below.
 */
export function PluginDetail({
  plugin,
  local,
  busy,
  connected = false,
  backLabel,
  phone = false,
  onBack,
  onInstall,
  onToggle,
  onUninstall,
  onContributionToggle,
  onCommandUnsandboxed,
  onApproveUpdate,
  onPublish,
  onRefreshLocal,
  settings,
}: {
  plugin: PluginCatalogItem | InstalledPlugin;
  local: InstalledPlugin | undefined;
  busy: boolean;
  /** Whether the hub has an account of its services. */
  connected?: boolean;
  backLabel: string;
  phone?: boolean;
  onBack: () => void;
  onInstall: (plugin: PluginCatalogItem) => void;
  onToggle: (plugin: InstalledPlugin) => void;
  onUninstall: (plugin: InstalledPlugin) => void;
  onContributionToggle: (plugin: InstalledPlugin, key: string, enabled: boolean) => void;
  /** Lets one of its commands run without a sandbox on this machine (Windows), or stops it. */
  onCommandUnsandboxed: (plugin: InstalledPlugin, command: string, allowed: boolean) => void;
  /** Installs a plugin's waiting update, approving what it adds. */
  onApproveUpdate: (plugin: InstalledPlugin) => void;
  /** Uploads a plugin built from a folder to the Crowdex Cloud account. */
  onPublish: (plugin: InstalledPlugin) => void;
  /** Builds a plugin imported from a folder of this computer again from that folder. */
  onRefreshLocal?: ((plugin: InstalledPlugin) => void) | undefined;
  /** The installed plugin's settings, shown in its page. */
  settings?: ReactNode;
}) {
  const router = useRouter();
  // A connection type of another plugin (`<plugin>/<service>`) is connected in that plugin's page.
  const accounts =
    local === undefined
      ? { own: [], rows: [] }
      : pluginAccounts(local, (service) => {
          const owner = service.includes('/') ? service.slice(0, service.lastIndexOf('/')) : null;
          router.push(owner ? pluginPagePath('installed', owner) : servicePluginPagePath(service));
        });
  const accountCount = accounts.own.length + accounts.rows.length;
  const crowdex = local?.crowdex;
  const configurable =
    local !== undefined && (isPluginConfigurable(local) || isPluginSourceConfigurable(local));
  // The services whose actions the plugin serves, and whether each connector is on (`null`: the
  // plugin is not installed, or does not bring it).
  const connectorState = (service: string): boolean | null => {
    if (local === undefined) return null;
    if ((local.connectors ?? []).some((connector) => connector.service === service)) return true;
    if (
      (local.inactiveContributions?.connectors ?? []).some(
        (connector) => connector.service === service,
      )
    )
      return false;
    return null;
  };
  const actionServices = [
    ...new Set([
      ...(plugin.service ? [plugin.service] : []),
      ...(local?.connectors ?? []).map((connector) => connector.service),
      ...(local?.inactiveContributions?.connectors ?? []).map((connector) => connector.service),
    ]),
  ]
    .filter((service) => !service.includes('/'))
    .map((service) => ({ service, enabled: connectorState(service) }));
  const single = actionServices.length === 1;
  const triggers = useServiceTriggers(local === undefined ? plugin.service : null);
  const brought = [
    ...broughtSummary(plugin, local).filter(
      // A service's connector is shown in "Connect and use", by way of connecting.
      (entry) =>
        !(plugin.service && local === undefined && entry.title === CONTRIBUTION_LABELS.connectors),
    ),
    // What its triggers wake a conversation on.
    ...(triggers.length > 0 ? [{ title: 'Wakes on', detail: triggers.join(', ') }] : []),
  ];
  const comingSoon = plugin.availability === 'coming-soon' && local === undefined;
  const connectable = local?.enabled && isPluginConnectionConfigurable(local);

  // The one main button: Install (and connect), then Connect while an account is missing.
  const main =
    local === undefined
      ? {
          label: comingSoon
            ? 'Coming soon'
            : needsAccount(plugin)
              ? `Install and connect ${plugin.displayName}`
              : 'Install',
          hint: comingSoon
            ? 'None of its ways to connect can be used yet.'
            : needsAccount(plugin)
              ? `You'll authorize access to ${plugin.displayName} in your browser`
              : null,
          disabled: busy || comingSoon,
          onClick: () => onInstall(plugin),
        }
      : connectable && !connected
        ? {
            label: `Connect ${plugin.displayName}`,
            hint: 'Agents and automations act with this account',
            disabled: busy,
            onClick: () =>
              document.getElementById(CONNECT_ID)?.scrollIntoView({ behavior: 'smooth' }),
          }
        : local.enabled && crowdex?.page !== undefined
          ? // An app: it opens full screen, from here as from All apps.
            {
              label: `Open ${plugin.displayName}`,
              hint: null,
              disabled: false,
              onClick: () => openAppHome(local.name),
            }
          : null;
  const mainButton = main && (
    <div className={cn(phone ? 'space-y-2 text-center' : 'mb-3 space-y-2 text-center')}>
      <button
        className="flex h-11 w-full items-center justify-center gap-2 rounded-control bg-primary px-4 font-semibold text-primary-foreground text-sm transition-colors hover:bg-primary/90 disabled:opacity-60 max-md:h-12 max-md:text-base"
        disabled={main.disabled}
        onClick={main.onClick}
        type="button"
      >
        {busy && <LoaderCircle className="size-4 animate-spin" />}
        {main.label}
      </button>
      {main.hint && <p className="text-muted-foreground text-xs">{main.hint}</p>}
    </div>
  );

  const side = (
    <div className="space-y-3">
      {!phone && mainButton}
      {local && (
        <div className="flex items-center justify-between gap-3 rounded-2xl border bg-card px-4 py-3 text-sm">
          <span>
            {local.enabled ? 'On' : 'Off'}
            <span className="block text-muted-foreground text-xs">
              {local.enabled ? 'Agents and automations use it' : 'Installed, not used'}
            </span>
          </span>
          <Switch
            aria-label={`${local.enabled ? 'Disable' : 'Enable'} ${plugin.displayName}`}
            checked={local.enabled}
            disabled={busy}
            onCheckedChange={() => onToggle(local)}
          />
        </div>
      )}
      <dl className="rounded-2xl border bg-card px-4 py-1">
        <Fact label="Source">
          {SOURCE_LABELS[plugin.source]}
          {plugin.otherSources?.length
            ? ` (also ${plugin.otherSources.map((source) => SOURCE_LABELS[source]).join(', ')})`
            : ''}
        </Fact>
        {plugin.marketplace && (
          <Fact label="Marketplace">
            <a
              className="underline-offset-2 hover:underline"
              href={plugin.marketplace.repository.replace(/\.git$/, '')}
              rel="noreferrer"
              target="_blank"
            >
              {plugin.marketplace.repository.replace(/^https:\/\/github\.com\/|\.git$/g, '')}
            </a>
          </Fact>
        )}
        {plugin.version && <Fact label="Version">v{plugin.version.replace(/^v/, '')}</Fact>}
        {plugin.license && <Fact label="License">{plugin.license}</Fact>}
        {plugin.service && <Fact label="For">Me · or my organization</Fact>}
        <Fact label="Plugin">
          <code className="font-mono text-xs">{plugin.name}</code>
        </Fact>
        {local && local.installedAt > 0 && (
          <Fact label="Installed">{new Date(local.installedAt).toLocaleDateString()}</Fact>
        )}
      </dl>
      {(local || (plugin.sourceUrl !== '' && /^https:\/\//.test(plugin.sourceUrl))) && (
        <div className="flex flex-wrap gap-2">
          {local && isMarketplacePluginSource(plugin.source) && (
            <button
              aria-label={`Update ${plugin.displayName}`}
              className={settingButtonClass()}
              disabled={busy}
              onClick={() => onInstall(plugin)}
              type="button"
            >
              <RefreshCw className="size-3.5" /> Update
            </button>
          )}
          {local && onRefreshLocal && local.localSourcePath !== undefined && (
            <button
              className={settingButtonClass()}
              disabled={busy}
              onClick={() => onRefreshLocal(local)}
              title={local.localSourcePath}
              type="button"
            >
              <RefreshCw className="size-3.5" /> Refresh from its folder
            </button>
          )}
          {local && crowdex?.install === 'local' && (
            <button
              className={settingButtonClass()}
              disabled={busy}
              onClick={() => onPublish(local)}
              title="Upload it to your Crowdex Cloud account and install it there"
              type="button"
            >
              <Upload className="size-3.5" /> Publish to account
            </button>
          )}
          {plugin.sourceUrl !== '' && /^https:\/\//.test(plugin.sourceUrl) && (
            <a
              aria-label={`Open ${plugin.displayName} source`}
              className={settingButtonClass()}
              href={plugin.sourceUrl}
              rel="noreferrer"
              target="_blank"
            >
              <ExternalLink className="size-3.5" /> Source
            </a>
          )}
          {local && crowdex?.install !== 'provisioned' && (
            <button
              className={cn(settingButtonClass(), 'border-destructive/40 text-destructive')}
              disabled={busy}
              onClick={() => onUninstall(local)}
              type="button"
            >
              <Trash2 className="size-3.5" /> Uninstall
            </button>
          )}
        </div>
      )}
    </div>
  );

  return (
    // A sub-page of Settings › Plugins: the section's column gives its width and margins.
    <div className={cn(phone && 'pb-32')}>
      {/* A phone goes back from its header. */}
      {!phone && (
        <button
          className="flex items-center gap-1.5 text-muted-foreground text-sm hover:text-foreground"
          onClick={onBack}
          type="button"
        >
          <ArrowLeft className="size-4" /> {backLabel}
        </button>
      )}
      <header className="mt-4 flex items-center gap-4">
        <PluginLogo plugin={plugin} size="large" />
        <div className="min-w-0">
          <h1 className="type-section-title truncate text-foreground">{plugin.displayName}</h1>
          <p className="mt-0.5 truncate text-muted-foreground text-sm">
            {[
              pluginKindLabel(local ?? plugin),
              SOURCE_LABELS[plugin.source],
              phone && plugin.version ? `v${plugin.version.replace(/^v/, '')}` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
      </header>
      <div className={cn('mt-5 flex gap-8', phone && 'flex-col gap-0')}>
        <div className="min-w-0 flex-1">
          <p className="max-w-3xl text-base text-foreground leading-6">{plugin.description}</p>

          {(local?.importNotices?.length ?? 0) > 0 && (
            <ul className="mt-4 space-y-1 rounded-2xl bg-attention/10 p-3 text-attention text-xs">
              {local?.importNotices?.map((notice) => (
                <li key={notice}>{notice}</li>
              ))}
            </ul>
          )}
          {crowdex?.blocked && (
            <p className="mt-4 rounded-2xl bg-destructive/10 p-3 text-destructive text-xs">
              {crowdex.blocked}
            </p>
          )}
          {local && crowdex?.pending && (
            <div className="mt-4 space-y-3 rounded-2xl border bg-card p-4">
              <p className="text-sm">
                Version {crowdex.pending.version} is ready.{' '}
                {crowdex.pending.added.length > 0 ? 'It also asks to:' : ''}
              </p>
              {crowdex.pending.added.length > 0 && (
                <GrantList grants={crowdex.pending.added} label="What the update adds" />
              )}
              <button
                className={settingButtonClass('primary')}
                disabled={busy}
                onClick={() => onApproveUpdate(local)}
                type="button"
              >
                Approve update
              </button>
            </div>
          )}

          {brought.length > 0 && local === undefined && (
            <Section title="What it brings">
              <div className="grid gap-2.5 sm:grid-cols-3">
                {brought.map((entry) => (
                  <div className="rounded-2xl border bg-card px-3.5 py-3" key={entry.title}>
                    <p className="font-semibold text-sm">{entry.title}</p>
                    <p className="mt-0.5 line-clamp-2 text-muted-foreground text-xs">
                      {entry.detail}
                    </p>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {local && (accountCount > 0 || !plugin.service) && (
            <Section
              count={accountCount}
              id={actionServices.length > 0 ? undefined : CONNECT_ID}
              title="Connections"
            >
              {accountCount === 0 ? (
                <div className="flex items-center gap-3 rounded-2xl border bg-card px-4 py-3 text-sm">
                  <Check className="size-4 text-success" />
                  <span>
                    <span className="block">Ready to use</span>
                    <span className="text-muted-foreground text-xs">
                      This plugin needs no connection or credential.
                    </span>
                  </span>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* A type of the plugin's own is its own card, never nested inside another one. */}
                  {accounts.own.map((service) => (
                    <HubConnections service={service} key={service} />
                  ))}
                  {accounts.rows.length > 0 && (
                    <div className="overflow-hidden rounded-2xl border bg-card">
                      {accounts.rows.map((account) => (
                        <AccountRowView account={account} busy={busy} key={account.key} />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Section>
          )}

          {actionServices.length > 0 && (
            <Section id={CONNECT_ID} title="Connect and use">
              <div className="space-y-6">
                {actionServices.map(({ service, enabled }) => (
                  <div key={service}>
                    {!single && <h3 className="mb-3 text-sm">{serviceTitle(service)}</h3>}
                    <PluginAgentActions
                      headingLevel={single ? 3 : 4}
                      service={service}
                      busy={busy}
                      // Accounts are connected once it is installed: its main button says so.
                      methodHeader={(method) =>
                        local === undefined ? null : (
                          // The connection types follow what is enabled: disabling reloads them.
                          <PluginServiceConnections
                            service={service}
                            key={String(local.enabled)}
                            method={method}
                          />
                        )
                      }
                      {...(local ? { onToggle: onContributionToggle, plugin: local } : {})}
                      {...(local && enabled !== null
                        ? {
                            allEnabled: enabled,
                            onToggleAll: (checked: boolean) =>
                              onContributionToggle(
                                local,
                                pluginContributionKey.connector(service),
                                checked,
                              ),
                          }
                        : {})}
                    />
                  </div>
                ))}
              </div>
            </Section>
          )}

          {local && (crowdex?.provides.length ?? 0) > 0 && (
            <Section title="Used for">
              <PluginUsedFor busy={busy} plugin={local} />
            </Section>
          )}

          {local && crowdex?.unsandboxedCommands && (
            <Section count={crowdex.unsandboxedCommands.length} title="Commands on this computer">
              <UnsandboxedCommands
                busy={busy}
                commands={crowdex.unsandboxedCommands}
                onChange={(command, allowed) => onCommandUnsandboxed(local, command, allowed)}
                pluginTitle={plugin.displayName}
              />
            </Section>
          )}

          {configurable && settings && (
            <Section id={SETTINGS_ID} title="Settings">
              {settings}
            </Section>
          )}

          {local && (
            <Section title="What it brings">
              <PluginContents busy={busy} onToggle={onContributionToggle} plugin={local} />
            </Section>
          )}
          {crowdex && crowdex.permissions.length > 0 && (
            <Section count={crowdex.permissions.length} title="Permissions">
              <ul aria-label="Permissions" className="flex flex-wrap gap-1.5">
                {crowdex.permissions.map((permission) => (
                  <li className="rounded-control bg-control px-3 py-1 text-xs" key={permission}>
                    {permissionLabel(permission)}
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {local && ((crowdex?.mcp.length ?? 0) > 0 || (crowdex?.provides.length ?? 0) > 0) && (
            <Section title="Diagnostics">
              <PluginDiagnostics connected={connected} plugin={local} />
            </Section>
          )}
          {phone && <div className="mt-7">{side}</div>}
        </div>
        {!phone && <aside className="w-72 shrink-0">{side}</aside>}
      </div>
      {phone && mainButton && (
        <div className="fixed inset-x-0 bottom-0 z-10 border-hairline border-t bg-background px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          {mainButton}
        </div>
      )}
    </div>
  );
}
